// src/components/Toast.js - Professional toast notification system
import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Toast Context for global toast management
 */
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

/**
 * Toast Provider - Wrap your app with this
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const MAX_TOASTS = 5; // Prevent excessive toast stacking

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000, action = null) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, action };

    setToasts(prev => {
      const newToasts = [...prev, toast];
      // Keep only the most recent MAX_TOASTS
      return newToasts.slice(-MAX_TOASTS);
    });

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((message, duration = 4000) =>
    showToast(message, 'success', duration), [showToast]);

  const error = useCallback((message, duration = 5000) =>
    showToast(message, 'error', duration), [showToast]);

  const warning = useCallback((message, duration = 4500) =>
    showToast(message, 'warning', duration), [showToast]);

  const info = useCallback((message, duration = 4000) =>
    showToast(message, 'info', duration), [showToast]);

  const withAction = useCallback((message, type, actionLabel, onAction, duration = 6000) => {
    return showToast(message, type, duration, { label: actionLabel, onClick: onAction });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info, withAction, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Toast Container - Renders all active toasts
 */
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div
      className="toast-container"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            toast={toast}
            index={index}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

/**
 * Individual Toast Component
 */
const Toast = ({ toast, index, onClose }) => {
  const { type, message, action } = toast;

  const config = {
    success: {
      icon: CheckCircleIcon,
      color: '#10b981',
      bg: '#f0fdf4',
      border: '#86efac'
    },
    error: {
      icon: ErrorIcon,
      color: '#ef4444',
      bg: '#fef2f2',
      border: '#fca5a5'
    },
    warning: {
      icon: WarningIcon,
      color: '#f59e0b',
      bg: '#fffbeb',
      border: '#fcd34d'
    },
    info: {
      icon: InfoIcon,
      color: '#3b82f6',
      bg: '#eff6ff',
      border: '#93c5fd'
    }
  };

  const { icon: Icon, color, bg, border } = config[type] || config.info;

  return (
    <motion.div
      className="toast"
      initial={{ opacity: 0, y: 50, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, x: 400, transition: { duration: 0.2 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{
        backgroundColor: bg,
        borderLeft: `4px solid ${color}`,
        border: `1px solid ${border}`,
        marginBottom: index > 0 ? '12px' : '0'
      }}
      role="alert"
      aria-live="assertive"
    >
      <div className="toast-content">
        <Icon style={{ color, fontSize: 22, flexShrink: 0 }} />
        <span className="toast-message">{message}</span>

        {action && (
          <button
            className="toast-action-btn"
            onClick={() => {
              action.onClick();
              onClose();
            }}
            style={{ color }}
          >
            {action.label}
          </button>
        )}

        <button
          className="toast-close-btn"
          onClick={onClose}
          aria-label="Close notification"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </button>
      </div>
    </motion.div>
  );
};
